import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Crown, Plus, Send, Loader2, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CAVEAT = "HMRC Control is a decision-support tool. It does not file returns for you and is not a substitute for advice from an ICAEW / CIOT registered accountant. Always confirm figures before submission.";

const HmrcChat = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/hmrc/chat/sessions`, { withCredentials: true }),
      axios.get(`${API}/api/hmrc/companies`, { withCredentials: true }),
    ]).then(([s, c]) => { setSessions(s.data); setCompanies(c.data); });
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streaming]);

  const loadSession = async (sessionId) => {
    setActiveSession(sessionId);
    const res = await axios.get(`${API}/api/hmrc/chat/sessions/${sessionId}/messages`, { withCredentials: true });
    setMessages(res.data);
  };

  const newSession = async () => {
    const res = await axios.post(`${API}/api/hmrc/chat/sessions`, { company_id: selectedCompany || null, title: 'New conversation' }, { withCredentials: true });
    setSessions(prev => [res.data, ...prev]);
    setActiveSession(res.data.id);
    setMessages([]);
    return res.data.id;
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    let sessionId = activeSession;
    if (!sessionId) { sessionId = await newSession(); }

    const text = input;
    setInput('');
    setSending(true);
    setStreaming('');
    setMessages(prev => [...prev, { role: 'user', content: text, created_at: new Date().toISOString() }]);

    try {
      const response = await fetch(`${API}/api/hmrc/chat/sessions/${sessionId}/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ text, document_ids: [] }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split('\n')) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'delta') { fullText += data.content; setStreaming(fullText); }
              else if (data.type === 'done') { setMessages(prev => [...prev, { role: 'assistant', content: fullText, created_at: new Date().toISOString() }]); setStreaming(''); }
              else if (data.type === 'error') { setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${data.content}`, created_at: new Date().toISOString() }]); setStreaming(''); }
            } catch (e) { console.error('SSE parse error:', e); }
          }
        }
      }
      const sessRes = await axios.get(`${API}/api/hmrc/chat/sessions`, { withCredentials: true });
      setSessions(sessRes.data);
    } catch (err) { console.error(err); setStreaming(''); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FAF9F7' }} data-testid="hmrc-chat">
      <div className="px-6 py-2" style={{ backgroundColor: '#FAF7F2', borderBottom: '1px solid #E8E0D4' }}>
        <p className="text-xs text-center" style={{ color: '#8B7355' }}>{CAVEAT}</p>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0 flex flex-col bg-white" style={{ borderRight: '1px solid #E5E1DB' }}>
          <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E1DB' }}>
            <button onClick={() => navigate('/hmrc')} className="flex items-center gap-1.5 text-xs" style={{ color: '#8B7355' }}>
              <Crown className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} /> HMRC
            </button>
            <button onClick={newSession} className="p-1.5 rounded-sm hover:bg-amber-50" data-testid="new-chat-btn"><Plus className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
          </div>
          <div className="px-3 py-2" style={{ borderBottom: '1px solid #E5E1DB' }}>
            <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} className="w-full px-2 py-1.5 text-xs bg-transparent border rounded-sm" style={{ borderColor: '#E5E1DB', color: '#8B7355' }} data-testid="company-filter">
              <option value="">All Companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto">
            {sessions.map(s => (
              <button key={s.id} onClick={() => loadSession(s.id)}
                className="w-full px-4 py-3 text-left hover:bg-amber-50/30 transition-colors"
                style={{ backgroundColor: activeSession === s.id ? '#FAF7F2' : 'transparent', borderBottom: '1px solid #F5F1EB' }}
                data-testid={`session-${s.id}`}>
                <p className="text-xs truncate" style={{ color: activeSession === s.id ? '#2C1A0E' : '#8B7355' }}>{s.title}</p>
                <p className="text-[10px] mt-0.5" style={{ color: '#B8860B' }}>{new Date(s.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && !streaming && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3" style={{ color: '#E5E1DB' }} />
                  <p className="text-sm" style={{ color: '#8B7355' }}>Ask Sterling anything about UK tax, HMRC, or Companies House.</p>
                  <p className="text-xs mt-1" style={{ color: '#B8860B' }}>Upload documents for AI review. Select a company for context.</p>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.created_at || `msg-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${i}`}>
                <div className="max-w-[75%] px-4 py-3 rounded-sm" style={{
                  backgroundColor: m.role === 'user' ? '#2C1A0E' : '#FFFFFF',
                  border: m.role === 'user' ? 'none' : '1px solid #E5E1DB',
                  color: m.role === 'user' ? '#F5F0E8' : '#2C1A0E',
                }}>
                  {m.role === 'assistant' && <p className="text-[10px] uppercase tracking-wider mb-1.5 font-semibold" style={{ color: '#D4AF37' }}>Sterling</p>}
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
            {streaming && (
              <div className="flex justify-start" data-testid="streaming-msg">
                <div className="max-w-[75%] px-4 py-3 rounded-sm bg-white border" style={{ borderColor: '#E5E1DB' }}>
                  <p className="text-[10px] uppercase tracking-wider mb-1.5 font-semibold" style={{ color: '#D4AF37' }}>Sterling</p>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#2C1A0E' }}>{streaming}<span className="animate-pulse" style={{ color: '#D4AF37' }}>|</span></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 bg-white" style={{ borderTop: '1px solid #E5E1DB' }}>
            <div className="flex items-end gap-2 max-w-3xl mx-auto">
              <div className="flex-1 border rounded-sm" style={{ borderColor: '#E5E1DB' }}>
                <textarea value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Ask Sterling about tax, HMRC, debts, filings..."
                  rows={1} className="w-full px-4 py-3 text-sm bg-transparent resize-none focus:outline-none"
                  style={{ color: '#2C1A0E', minHeight: '44px', maxHeight: '120px' }}
                  data-testid="chat-input" />
              </div>
              <button onClick={sendMessage} disabled={sending || !input.trim()} className="p-3 rounded-sm disabled:opacity-30 flex-shrink-0" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HmrcChat;
